import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { HeartPulse, RefreshCw } from "lucide-react";
import { formatDateTime } from "@/lib/formatDate";

const TIER_LABELS = { sol: "Sol (Flagship)", terra: "Terra (Standard)", luna: "Luna (Schnell)" };

export default function AiHealthDashboardCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/ai-health");
      setData(res.data);
    } catch (e) {
      console.error("ai-health load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <Card className="p-4 border border-slate-200 bg-white shadow-sm mt-4" data-testid="ai-health-card">
        <div className="text-sm text-slate-400">{loading ? "Lade KI-Gesundheit …" : "KI-Gesundheit nicht verfügbar."}</div>
      </Card>
    );
  }

  const c = data.consensus || {};
  const total = c.total || {};
  const recent = c.last_30_days || {};

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm mt-4" data-testid="ai-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="h-4 w-4 text-rose-500" />
          <h2 className="text-sm font-semibold text-slate-900">KI-Gesundheit</h2>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="ai-health-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>

      {/* Aktive Modelle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {Object.entries(data.models || {}).map(([provider, m]) => (
          <div key={provider} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`ai-health-model-${provider}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{provider}</div>
            <div className="text-sm font-mono font-medium text-slate-900">{m.active_model || "—"}</div>
            {provider === "openai" && m.tier_models && Object.keys(m.tier_models).length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {Object.entries(m.tier_models).map(([tier, model]) => (
                  <div key={tier} className="text-[11px] text-slate-600 flex justify-between">
                    <span>{TIER_LABELS[tier] || tier}</span>
                    <span className="font-mono">{model}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] text-slate-400 mt-1.5">
              {m.checked_at ? `Geprüft: ${formatDateTime(m.checked_at)}` : "Noch kein Modell-Check gelaufen"}
              {m.available_count ? ` · ${m.available_count} Modelle verfügbar` : ""}
            </div>
          </div>
        ))}
      </div>

      {/* Konsens-Statistik */}
      <div className="rounded-lg border border-slate-200 p-3 mb-4" data-testid="ai-health-consensus">
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Mehrfach-Prüfung der Belege (Konsens) — {c.checked_total || 0} geprüft
          {c.checked_total > 0 && (
            <span className="ml-2 font-normal text-slate-500">
              Abweichungsquote: {((c.mismatch_rate || 0) * 100).toFixed(1)}%
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="ai-health-consensus-confirmed">
            ✓ Bestätigt: {total.confirmed || 0} (30 Tage: {recent.confirmed || 0})
          </Badge>
          <Badge className="bg-sky-50 text-sky-700 border border-sky-200" data-testid="ai-health-consensus-resolved">
            ⚖ Von 3. KI entschieden: {total.mismatch_resolved || 0} (30 Tage: {recent.mismatch_resolved || 0})
          </Badge>
          <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="ai-health-consensus-unresolved">
            ⚠ Ungeklärt (manuell): {total.mismatch_unresolved || 0} (30 Tage: {recent.mismatch_unresolved || 0})
          </Badge>
        </div>
      </div>

      {/* Störungen */}
      <div className="rounded-lg border border-slate-200 p-3 mb-4" data-testid="ai-health-failures">
        <div className="text-xs font-semibold text-slate-700 mb-2">Modell-Störungen (Selbstheilung)</div>
        {(data.health || []).length === 0 ? (
          <div className="text-xs text-emerald-700">✓ Keine Störungen registriert — alle Modelle stabil.</div>
        ) : (
          <div className="space-y-1">
            {data.health.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="font-mono text-slate-700">{h.provider}:{h.model}</span>
                <span className={h.recent_failures > 0 ? "text-amber-700" : "text-slate-400"}>
                  {h.recent_failures} Fehler (24h){h.last_error ? ` · ${h.last_error.slice(0, 60)}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ereignis-Historie */}
      <div className="rounded-lg border border-slate-200 p-3" data-testid="ai-health-events">
        <div className="text-xs font-semibold text-slate-700 mb-2">Modellwechsel & Warnungen (Historie)</div>
        {(data.events || []).length === 0 ? (
          <div className="text-xs text-slate-400">Noch keine Ereignisse — beim nächsten Modellwechsel erscheint hier der Eintrag (+ Mail an Admins).</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {data.events.map((e, i) => (
              <div key={i} className="flex items-start justify-between gap-2 text-xs border-b border-slate-100 pb-1">
                <span className={e.kind === "model_health" ? "text-amber-800" : "text-slate-700"}>
                  {e.subject}
                </span>
                <span className="shrink-0 text-slate-400">
                  {formatDateTime(e.created_at)} {e.email_sent ? "📧" : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
